import { FC, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useYearQuarterNavigation } from 'hooks';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { InferType } from 'yup';

import { Baseplan, PlannedHour } from 'components/types';

import { scenarioAtom } from '../../atoms';

import LoadingButton from '@mui/lab/LoadingButton';
import { Button, DialogActions } from '@mui/material';

import { DatePicker, Select } from 'components/shared';

import { useSaveAndUpdateMutation } from '../queries';
import addPlannedHoursDialogSchema from './add-planned-hours-dialog-schema';
import { StyledDialogMedium } from 'components/shared/Dialog/Dialog.styles';
import {
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';

interface PlannedHoursDialogProps {
  editValues: any;
  isOpen: boolean;
  handleClose: () => void;
  options: Baseplan[] | null;
  plannedHours: PlannedHour[];
  minDate: Date | undefined;
  maxDate: Date | undefined;
  loading: boolean;
}

const PlannedHoursDialog: FC<PlannedHoursDialogProps> = ({
  editValues,
  isOpen,
  handleClose,
  options,
  plannedHours,
  minDate,
  maxDate,
  loading
}) => {
  const { t } = useTranslation('common');
  const scenario = useRecoilValue(scenarioAtom);
  const { year } = useYearQuarterNavigation();
  const schema = addPlannedHoursDialogSchema(plannedHours, editValues);
  type Form = InferType<typeof schema>;

  const { control, handleSubmit, setValue } = useForm<Form>({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (editValues) {
      setValue('baseplanId', editValues.baseplanId);
      setValue('startDate', new Date(editValues.startDate));
    } else {
      setValue('baseplanId', undefined);
      setValue('startDate', new Date(year, 0, 1));
    }
  }, [editValues]);

  const { mutate: saveAndUpdate } = useSaveAndUpdateMutation();

  const isEditMode = !!editValues;

  const onSubmit = async (data) => {
    if (!scenario) {
      return;
    }

    const { baseplanId, ...rest } = data;

    let hours = [...plannedHours];
    if (isEditMode) {
      hours = hours.filter(
        ({ startDate }) => startDate !== editValues.startDate
      );
    }

    const payload = [
      { baseplanId, ...rest },
      ...hours.map(({ baseplanId, startDate }) => ({ baseplanId, startDate }))
    ];
    await saveAndUpdate({
      scenarioId: scenario.scenarioId,
      year,
      payload
    });

    handleClose();
  };

  return (
    <StyledDialogMedium
      open={isOpen}
      title={isEditMode ? t('edit') : t('baseplan.plannedHoursDialog.title')}
      handleClose={handleClose}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <GridContainer>
          <GridCompact6>
            <Controller
              name="baseplanId"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  fullWidth
                  {...field}
                  onSelected={field.onChange}
                  label={t('baseplan.plannedHoursDialog.selectBaseplan')}
                  options={
                    options
                      ? options.map(({ name, baseplanId }) => ({
                          label: `${name}`,
                          value: baseplanId
                        }))
                      : [{ label: 'no plans', value: '' }]
                  }
                  error={fieldState.error}
                  valueProp={field.value}
                  required
                />
              )}
            />
          </GridCompact6>
          <GridCompact6>
            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  date={field.value}
                  label={t('startDate')}
                  minDate={minDate}
                  maxDate={maxDate}
                  error={fieldState.error}
                  required
                />
              )}
            />
          </GridCompact6>
        </GridContainer>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <LoadingButton loading={loading} type="submit" variant="contained">
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </StyledDialogMedium>
  );
};

export default PlannedHoursDialog;
